import { add } from 'date-fns';

import { ClientInputError, ClientWalletError } from '@/lib/errors/client';

import { CHAIN_IDS } from '@/constants/chain';

import { submitLimitOrderCore } from '@/hooks/useSubmitLimitOrder';

import { getWalletClientAfterChainSwitch } from '@/utils/wallet';

import { submitAdvancedOrder } from '../submitAdvancedOrder';
import type { TradeStrategy } from '../types';

/** Advanced-order build, sign, and orderbook submission strategy. */
export const advancedStrategy: TradeStrategy = {
  lane: 'advanced',
  preflight: (ctx) => {
    if (
      ctx.quote.source !== 'advanced' ||
      ctx.quote.status !== 'derived' ||
      !ctx.advanced ||
      !ctx.accountAddress
    ) {
      return null;
    }
    if (ctx.chainId === CHAIN_IDS.SOLANA) {
      return ctx.solanaWallet ? { chainFamily: 'solana' } : null;
    }
    return ctx.ethereumWallet ? { chainFamily: 'evm' } : null;
  },
  submit: async (ctx, prepared) => {
    const advanced = ctx.advanced;
    const accountAddress = ctx.accountAddress;
    const result = await submitAdvancedOrder({
      chainId: ctx.chainId,
      sellToken: ctx.sellToken,
      buyToken: ctx.buyToken,
      sellInput: ctx.sellInput,
      buyInput: { ...ctx.buyInput, decimal: advanced?.buyAmountDecimal ?? ctx.buyInput.decimal },
      orderType: advanced?.orderType ?? 'limit',
      matchaReferrer: ctx.referrer.id,
      validate: () => {
        if (!advanced || !accountAddress) {
          throw new ClientInputError('submitLimitOrder: missing advanced order context');
        }
      },
      refreshOpenLimitOrders: (identity) => {
        if (accountAddress) ctx.deps.refreshOpenLimitOrders(accountAddress, identity);
      },
      setRecentTransaction: () => ctx.deps.setRecentTransaction(true),
      submit: async () => {
        if (!advanced || !accountAddress) {
          throw new ClientInputError('submitLimitOrder: missing advanced order context');
        }
        // 'custom' with no picked date would otherwise parse to NaN and POST an Invalid Date expiry.
        const expiry =
          advanced.expiration === 'custom'
            ? advanced.customExpiration
            : add(new Date(), { minutes: parseInt(advanced.expiration, 10) });
        if (!expiry || Number.isNaN(expiry.getTime())) {
          throw new ClientInputError('submitLimitOrder: invalid expiration');
        }

        const variables = {
          chainId: ctx.chainId,
          maker: accountAddress,
          buyToken: ctx.buyToken,
          sellToken: ctx.sellToken,
          buyAmount: advanced.buyAmountDecimal,
          sellAmount: ctx.sellInput.decimal,
          expiry,
          orderType: advanced.orderType,
          triggerCrossPrice: advanced.triggerCrossPrice,
        };
        switch (prepared.chainFamily) {
          case 'evm': {
            const client = await getWalletClientAfterChainSwitch(ctx.chainId);
            if (!client || !client.account) {
              throw new ClientWalletError(
                'Wallet connection error. Please try again or reconnect your wallet.',
              );
            }
            if (client.chain?.id !== ctx.chainId) {
              throw new ClientWalletError('Wallet client chain does not match the Advanced order');
            }
            return submitLimitOrderCore(variables, {
              chainFamily: 'evm',
              chainId: ctx.chainId,
              walletClient: client,
              publicClient: ctx.publicClient,
              setPhase: () => undefined,
            });
          }
          case 'solana':
            if (ctx.chainId !== CHAIN_IDS.SOLANA || !ctx.solanaWallet) {
              throw new ClientWalletError('Solana wallet connection is unavailable');
            }
            return submitLimitOrderCore(variables, {
              chainFamily: 'svm',
              chainId: CHAIN_IDS.SOLANA,
              solanaWallet: ctx.solanaWallet,
              signMessage: ctx.signSolanaMessage,
              signTransaction: ctx.signSolanaTransaction,
              setPhase: () => undefined,
            });
          default: {
            const exhaustiveFamily: never = prepared.chainFamily;
            return exhaustiveFamily;
          }
        }
      },
    });
    if (!result.success) return result;

    ctx.deps.resetLimitRate();
    ctx.deps.resetInputs();
    return {
      success: true,
      outcome: {
        kind: result.submission.provider === 'definitive' ? 'orderId' : 'orderHash',
        value: result.submission.orderIdentifier,
      },
    };
  },
};
